import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Spinner,
  useToast,
  Checkbox,
} from "@chakra-ui/react";
import { Search, ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getDriversList } from "../../actions/inventory/driver.action";
import { dispatchTripToAllDrivers } from "../../actions/dispatch.actions";
import { Loading } from "../../components/Loading";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";

const AllDrivers = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { tripId, tripData } = location.state || {};
  const bookingId = tripData?._id || tripData?.mongoId || tripData?.id || tripId;

  const [searchInput, setSearchInput] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [dispatching, setDispatching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDrivers, setTotalDrivers] = useState(0);

  const DRIVER_STATUS_LABEL = {
    0: "Offline",
    1: "Available",
    2: "Busy",
    3: "Suspended",
  };

  const SortIcon = () => (
    <Box ml="4px" display="flex" flexDirection="column">
      <TriangleUpIcon boxSize="7px" color="#CBD5E1" />
      <TriangleDownIcon boxSize="7px" color="#CBD5E1" mt="1px" />
    </Box>
  );

  useEffect(() => {
    loadDrivers();
  }, [currentPage, itemsPerPage]);

  const vehicleSegmentId =
    tripData?.vehicleSegment?._id ||
    (typeof tripData?.vehicleSegment === "string" ? tripData.vehicleSegment : null);

  const loadDrivers = async () => {
    setFetching(true);
    try {
      const response = await getDriversList(
        currentPage,
        itemsPerPage,
        searchInput,
        "",
        vehicleSegmentId
      );
      setDrivers(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotalDrivers(response.totalDrivers || 0);
    } catch (error) {
      console.error("Error loading drivers:", error);
      toast({
        title: "Failed to fetch drivers",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFetching(false);
    }
  };

  const handleSelectDriver = (driverId) => {
    setSelectedDrivers((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId],
    );
  };

  const handleSelectAll = () => {
    if (selectedDrivers.length === drivers.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(drivers.map((d) => d._id));
    }
  };

  const handleDispatchToSelected = async () => {
    if (selectedDrivers.length === 0) {
      toast({
        title: "No drivers selected",
        description: "Please select at least one driver",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setDispatching(true);
    try {
      const res = await dispatchTripToAllDrivers({
        bookingId,
        tripData,
        driverIds: selectedDrivers,
      });

      toast({
        title: "Drivers notified",
        description:
          res?.message ||
          `Booking details sent to ${selectedDrivers.length} drivers.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/dispatch/available-drivers", {
        state: {
          tripId: tripData?._id || tripId,
          tripData,
          shortlistedDriverIds: selectedDrivers,
        },
      });
    } catch (error) {
      toast({
        title: "Dispatch failed",
        description:
          error.message || "Could not send booking details to drivers",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDispatching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      loadDrivers();
    }
  };

  const standardOptions = [9, 20, 50];
  const perPageOptions = (() => {
    if (totalDrivers === 0) return standardOptions;
    if (totalDrivers < standardOptions[0]) return [totalDrivers];
    const filtered = standardOptions.filter((num) => num <= totalDrivers);
    return filtered;
  })();

  return (
    <Box minH="100vh" p={8}>
      <Box borderRadius="12px" p={6} boxShadow="sm">
        {/* Header Section */}
        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={4}>
            <Button
              bg="transparent"
              p={0}
              minW="auto"
              h="auto"
              _hover={{ bg: "transparent" }}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} color="#333B69" />
            </Button>
            <Box>
              <Text
                fontFamily="inter, sans-serif"
                fontWeight="600"
                fontSize="22px"
                color="#333B69"
              >
                Dispatch to All Drivers
              </Text>
              <Text
                fontSize="14px"
                fontWeight="500"
                color="#174277"
                fontFamily="inter, sans-serif"
                mt={1}
              >
                Trip ID: {tripId || "N/A"} | Vehicle Type:{" "}
                {tripData?.vehicleType || "N/A"}
              </Text>
            </Box>
          </Flex>
          <Button
            bg="#396AFF"
            color="white"
            borderRadius="8px"
            px={6}
            py={3}
            fontSize="14px"
            fontWeight="600"
            height="40px"
            fontFamily="inter, sans-serif"
            _hover={{ bg: "#2952CC" }}
            onClick={handleDispatchToSelected}
            isLoading={dispatching}
            isDisabled={selectedDrivers.length === 0}
          >
            Shortlist to {selectedDrivers.length} Selected Driver
            {selectedDrivers.length !== 1 ? "s" : ""}
          </Button>
        </Flex>

        {/* Search Bar */}
        <Box mb={6}>
          <InputGroup maxW="250px">
            <InputLeftElement pointerEvents="none" h="40px">
              <Search size={16} color="#64748B" />
            </InputLeftElement>
            <Input
              placeholder="Search Driver"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              borderRadius="md"
              h="40px"
              bg="#FFFF"
              border="1px solid #E2E8F0"
              color="#64748B"
              fontSize="14px"
              _placeholder={{ color: "#64748B" }}
              _focus={{ outline: "none", boxShadow: "0 0 0 1px" }}
            />
          </InputGroup>
        </Box>

        <Box bg="white" borderRadius="12px" p={6} boxShadow="sm">
          {/* Loading Indicator */}
          {fetching && <Loading />}

          {/* Table */}
          {!fetching && (
            <Box overflowX="auto" mb={6}>
              <Table variant="simple" bg="white" fontFamily="inter, sans-serif">
                <Thead>
                  <Tr>
                    <Th
                      textTransform="none"
                      color="#718EBF"
                      fontSize="16px"
                      fontWeight="500"
                      py={4}
                      borderBottom="2px solid #E5E5E5"
                    >
                      <Checkbox
                        isChecked={
                          selectedDrivers.length === drivers.length &&
                          drivers.length > 0
                        }
                        onChange={handleSelectAll}
                        colorScheme="blue"
                      />
                    </Th>
                    {[
                      "Driver Name",
                      "Vehicle No.",
                      "Mobile No.",
                      "Current Location",
                      "Rating",
                      "Status",
                    ].map((head) => (
                      <Th
                        key={head}
                        textTransform="none"
                        color="#718EBF"
                        fontSize="16px"
                        fontWeight="500"
                        py={4}
                        borderBottom="2px solid #E5E5E5"
                      >
                        <Flex align="center" gap={2}>
                          <Text>{head}</Text>
                          <SortIcon />
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                </Thead>

                <Tbody>
                  {drivers.length > 0 ? (
                    drivers.map((driver) => (
                      <Tr
                        key={driver._id}
                        _hover={{ bg: "#F8FAFC" }}
                        borderBottom="1px solid #E5E5E5"
                      >
                        <Td py={4} border="none">
                          <Checkbox
                            isChecked={selectedDrivers.includes(driver._id)}
                            onChange={() => handleSelectDriver(driver._id)}
                            colorScheme="blue"
                          />
                        </Td>
                        <Td py={4} border="none">
                          {driver.name || "N/A"}
                        </Td>
                        <Td py={4} border="none">
                          {driver.vehicleDetails?.vehicleNumber || "N/A"}
                        </Td>
                        <Td py={4} border="none">
                          {driver.contactDetails?.mobileNumber || "N/A"}
                        </Td>
                        <Td py={4} border="none">
                          {driver.currentLocation?.address || "N/A"}
                        </Td>
                        <Td py={4} border="none">
                          <Badge bg="#FEF3C7" color="#92400E">
                            ⭐ {driver.aditionalDetails?.driverRating || "N/A"}
                          </Badge>
                        </Td>
                        <Td py={4} border="none">
                          <Badge
                            bg={
                              DRIVER_STATUS_LABEL[
                                driver.aditionalDetails?.status
                              ] === "Available"
                                ? "#D1FAE5"
                                : "#FEF3C7"
                            }
                            color={
                              DRIVER_STATUS_LABEL[
                                driver.aditionalDetails?.status
                              ] === "Available"
                                ? "#059669"
                                : "#92400E"
                            }
                            fontSize="11px"
                            fontWeight="600"
                            px={3}
                            py={1.5}
                            borderRadius="4px"
                            textTransform="capitalize"
                          >
                            {DRIVER_STATUS_LABEL[
                              driver.aditionalDetails?.status
                            ] || "N/A"}
                          </Badge>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8}>
                        <Text color="#718EBF">No drivers found</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={2}>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                Showing:
              </Text>
              <Select
                size="sm"
                width="70px"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                borderRadius="6px"
                border="1px solid #E9E7FD"
                _focus={{ outline: "none", borderColor: "#00B69B" }}
              >
                {perPageOptions.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </Select>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                of{" "}
                <Text as="span" fontWeight="600" color="#8B909A">
                  {totalDrivers}
                </Text>
              </Text>
            </Flex>
            <Flex gap={2}>
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                &lt;
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    bg={pageNum === currentPage ? "#0F60FF" : "#F1F2F6"}
                    color={pageNum === currentPage ? "white" : "#8B909A"}
                    border="1px solid"
                    borderColor={
                      pageNum === currentPage ? "#0F60FF" : "#E9E7FD"
                    }
                    _hover={{
                      bg: pageNum === currentPage ? "#0F60FF" : "#E9E7FD",
                    }}
                    w="36px"
                    h="36px"
                    borderRadius="6px"
                    fontWeight="500"
                    fontFamily="'Public Sans', sans-serif"
                    fontSize="15px"
                    lineHeight="100%"
                    letterSpacing="0.43px"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default AllDrivers;
